<template>
  <!-- align-start: as ações acompanham a primeira linha do título. Centralizadas,
       elas desciam junto com o subtítulo e deixavam de se alinhar com o título. -->
  <div class="d-flex align-start flex-wrap page-header">
    <!-- O acento da coisa de que a tela trata, quando ela tem cor: hoje, o item. O mesmo tom
         da barrinha da raia do Kanban — a janela do item e a raia dele se reconhecem.

         De PÉ, ao lado do título: deitada em cima, com 3px, ela passava despercebida. E ao lado
         em vez de tingindo o texto, porque o identificador é o que mais se lê nesta tela e não
         paga legibilidade por ornamento. -->
    <ontos-color-accent v-if="accent" vertical :color="accent" class="page-header-accent"/>
    <div class="page-header-title mr-2">
      <slot name="title">
        <!-- title nativo: o texto cortado continua legível ao parar o ponteiro -->
        <component :is="tag" :class="headingClass" class="page-header-heading" :title="title">
          <v-icon v-if="icon" start :color="iconColor || undefined">{{ icon }}</v-icon>{{ title }}
        </component>
        <!-- Slot além da prop: há subtítulo com partes de peso diferente -->
        <div v-if="subtitle || $slots.subtitle" class="text-subtitle-2 text-grey page-header-subtitle"
             :class="{'page-header-subtitle--rich': !!$slots.subtitle}"
             :title="subtitle">
          <slot name="subtitle">{{ subtitle }}</slot>
        </div>
      </slot>
    </div>
    <!-- Um ml-auto só, no grupo — e não um por botão, que é o que hoje espalha as
         ações em vez de agrupá-las à direita. -->
    <div class="d-flex align-center flex-wrap ml-auto page-header-actions">
      <!-- As ações SOBRE o assunto da tela num bloco só (ver ActionGroup), em TODO cabeçalho do
           app e em qualquer largura (pedido do usuário: um padrão, e não botões soltos que só
           pareciam agrupados): só ícones abaixo do md, ícone e rótulo dele para cima (o
           HeaderAction encolhe sozinho), cada um com a cor dele. -->
      <ontos-action-group v-if="$slots.actions">
        <slot name="actions"/>
      </ontos-action-group>
      <!-- Os controles da JANELA — expandir, voltar, fechar, ir para a lista —, fora do bloco e
           sem moldura: não agem sobre o assunto, e o ✕ dentro dele leria como mais uma ação.

           E uma exceção, por decisão do usuário: o "Adicionar" das seções de relacionamento da
           ficha mora aqui também, solto, para ler igual ao "Criar" da faixa do caminho — que não
           é cabeçalho e não tem bloco. -->
      <slot name="controls"/>
    </div>
    <!-- Depois das ações, no fim da linha. Hoje só chega conteúdo aqui na largura de
         telefone: acima dela as visões da coleção ficam na faixa do caminho. -->
    <div v-if="$slots.tabs" class="page-header-tabs">
      <slot name="tabs"/>
    </div>
    <!-- Uma faixa da largura INTEIRA, embaixo da linha: o que é do título mas não cabe na coluna
         dele, que encolhe para dar lugar às ações. Na ficha, as falhas de um relacionamento —
         no subtítulo, espremidas pelos botões do quadro, quebravam em 330px com a linha vazia ao
         lado (relato do usuário). -->
    <div v-if="$slots.below" class="page-header-below">
      <slot name="below"/>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ColorAccent from '@/components/shared/ColorAccent.vue'
import ActionGroup from '@/components/shared/ActionGroup.vue'

/**
 * Cabeçalho de página: título à esquerda, ações agrupadas à direita.
 *
 * Usa flex direto em vez de v-row/v-col porque o alinhamento precisa de um
 * `ml-auto` único no grupo de ações. Com um por coluna — como as telas fazem hoje
 * à mão — o espaço livre é dividido entre os botões e eles se espalham.
 *
 * O título sai por classe de tipografia do Vuetify, e não pela tag: não há nenhuma
 * regra de h1/h2 no CSS do app, então uma tag crua renderiza no padrão do browser
 * (2em fixo), que é o que não cabe num telefone.
 *
 * A escala é 18px no xs (duas linhas, ver o estilo), text-h6 (20px) no sm e text-h5 (24px) do md
 * para cima — abaixo dos 32px da tag crua em todos.
 */
@Component({
  name: 'ontos-page-header',
  components: {'ontos-color-accent': ColorAccent, 'ontos-action-group': ActionGroup}
})
class PageHeader extends Vue {
  @Prop({type: String, default: ''}) readonly title!: string
  @Prop({type: String, default: ''}) readonly subtitle!: string
  /** A cor da coisa de que a tela trata; vazio na tela que não fala de coisa colorida. */
  @Prop({type: String, default: ''}) readonly accent!: string
  /** Ícone antes do título, usado pelos cabeçalhos de seção do design */
  @Prop({type: String, default: ''}) readonly icon!: string
  /** A cor do ícone, quando ele diz algo além do assunto — o vermelho da janela de recusa. */
  @Prop({type: String, default: ''}) readonly iconColor!: string
  /** Só a tag, para semântica: h1 nas páginas, h2 no modal do Kanban. */
  @Prop({type: [String, Number], default: 1}) readonly level!: string | number
  /** Cabeçalho de seção dentro da página: mesmo layout, tipografia menor. */
  @Prop({type: Boolean, default: false}) readonly dense!: boolean

  get tag(): string {
    return `h${this.level}`
  }

  //Tamanho separado do nível: o modal usa h2 e quer tamanho de título de página. O grande (h5) só
  //do md para cima: no sm — o tablet, o telefone deitado — ele já empurrava o título para as
  //reticências (relato do usuário), como os rótulos dos botões (ver HeaderAction.compact)
  get headingClass(): string {
    return this.dense ? 'text-subtitle-1' : 'text-h6 text-md-h5'
  }
}

export default toNative(PageHeader)
</script>

<style scoped>
/* Um respiro entre a régua e o título que ela marca. */
.page-header-accent {
  margin-right: 12px;
}

/* width: 100% e não largura automática: dentro de um container flex — v-card-title
   é um — este bloco vira item flex e encolheria até o conteúdo. Sem espaço livre o
   ml-auto das ações não tem o que distribuir, e os botões colam no título.
   Onde o cabeçalho é filho de um bloco comum, não muda nada. */
.page-header {
  width: 100%;
  gap: 8px;
  margin-bottom: 8px;
}

/* Com ABAS no lugar do título, as ações se alinham pelo centro, e não pelo topo: o topo é para
   acompanhar a primeira linha de um título com subtítulo, e abas não têm subtítulo. Empilhadas no
   telefone, elas medem 48px, e pelo topo o botão ao lado ficava preso em cima delas (relato do
   usuário). Com as de 28px, que já têm a altura dos botões, não muda nada. */
.page-header:has(> .page-header-title .v-tabs) {
  align-items: center !important;
}

/* flex: 1 1 0 e não largura automática: com base automática o título entra na conta
   da quebra pelo tamanho do texto, e um título longo empurrava os botões para a
   linha de baixo. Com base zero ele fica com o espaço que sobra — e o que não couber
   vira reticências, em vez de deslocar as ações. */
.page-header-title {
  min-width: 0;
  flex: 1 1 0;
}

/* Mede o próprio conteúdo e não encolhe: quem cede espaço é o título, que já tem
   reticências para isso. A separação dos botões é o gap da linha, sem traço nem folga
   extra. */
.page-header-tabs {
  flex: 0 0 auto;
}

/* Casa a altura da faixa de abas com a dos botões ao lado.

   Esta linha alinha pelo TOPO (align-start, para as ações acompanharem a primeira linha do
   título), então altura diferente vira texto desalinhado: os 36px da densidade compacta contra
   os 28px de um v-btn size="small" põem o texto da aba 4px abaixo do texto do botão. Casando a
   altura, os dois centros coincidem.

   **No DESCENDENTE, e não aqui na caixa.** O Vuetify declara `--v-tabs-height` no PRÓPRIO
   `.v-tabs` (pela classe da densidade), e valor herdado de um ancestral perde para declaração
   no elemento — pôr a variável no `.page-header-tabs` não muda nada. O `:deep` compila para
   `.page-header-tabs[data-v-…] .v-tabs`, que ganha da classe de densidade e não está na camada
   do Vuetify.

   Mora aqui, e não na faixa de abas, porque quem sabe que os vizinhos são botões de 28px é este
   cabeçalho — a mesma faixa, sozinha na barra do caminho, não tem com que se alinhar.

   Vale também para abas no lugar do TÍTULO, como no design do ambiente, e no lugar das AÇÕES, como
   a Lista/Kanban de um relacionamento na linha das abas da ficha: os vizinhos são os mesmos.

   Menos as EMPILHADAS (o ícone sobre o texto, no telefone — ver o `stacked` do ViewTabs): em 28px
   elas não cabem, e ficam com a altura que o Vuetify dá ao empilhado. */
.page-header-tabs :deep(.v-tabs:not(.v-tabs--stacked)),
.page-header-title :deep(.v-tabs:not(.v-tabs--stacked)),
.page-header-actions :deep(.v-tabs:not(.v-tabs--stacked)) {
  --v-tabs-height: 28px;
}

/* Zera a margem que o browser dá às tags de título: o espaçamento passa a ser o
   gap do flex, igual em qualquer nível.

   O peso é fixado em 500 porque a escala do Vuetify muda de peso junto com o
   tamanho — text-h6 é 500 e text-h5 é 400 —, então sem isto o título ficaria mais
   FINO justamente na tela maior. O !important é para vencer o das classes de
   tipografia.

   Uma linha só com reticências: o título é o que cede espaço para os botões, e
   cortar o texto é menos ruim do que empurrar as ações para fora da linha. */
.page-header-heading {
  margin: 0;
  line-height: 1.2;
  font-weight: 500 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* No telefone o título cabe mais: um degrau menor, e até duas linhas antes das reticências. Numa
   linha só, "Canção do Galo Capão" virava "Canção do Galo C…" ao lado dos botões (relato do
   usuário). O !important, como acima, para vencer o das classes de tipografia. */
@media (max-width: 599.98px) {
  .page-header-heading {
    font-size: 1.125rem !important;
    white-space: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
}

.page-header-subtitle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Vindo do slot, o subtítulo tem mais do que texto — o nome da coleção e, embaixo dele,
   um chip, na ficha da instância. As partes se empilham, cada uma na sua linha: lado a
   lado, o corte por reticências que serve ao texto cortaria o chip justamente na parte
   em que se clica.

   align-items: flex-start para o chip ficar do tamanho do conteúdo; esticado, ele viraria
   uma barra da largura do cabeçalho. O espaçamento é gap, pela mesma razão que rege o
   resto do componente: quem não for renderizado não deixa buraco. */
.page-header-subtitle--rich {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  white-space: normal;
  overflow: visible;
}

.page-header-actions {
  gap: 8px;
}

/* Base de 100%: no flex com quebra, é o que manda a faixa para uma linha só dela.

   A margem negativa desfaz o `gap` entre as linhas: a faixa continua o título, colada a ele como
   um subtítulo, e não é mais um item da linha, que pede respiro. Com o vão, ela se afastava do
   título (relato do usuário). O respiro, se quiser, vem de dentro do que se põe aqui. */
.page-header-below {
  flex: 1 0 100%;
  margin-top: -8px;
}
</style>
